import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = [
    "input", "dropzone", "progress", "list", "hiddenInputs",
    "galleryModal", "galleryCarouselInner", "galleryPrev", "galleryNext", "galleryTitle", "galleryCounter", "galleryDownload"
  ]
  static values = {
    name: String,
    url: String,
    attachableType: String,
    attachableId: String,
    attribute: String
  }

  openPicker(e) {
    e.preventDefault()
    this.inputTarget.click()
  }

  dragover(e) {
    e.preventDefault()
    this.dropzoneTarget.classList.add("border-primary", "bg-light")
  }

  dragleave() {
    this.dropzoneTarget.classList.remove("border-primary", "bg-light")
  }

  drop(e) {
    e.preventDefault()
    this.dragleave()

    if (!this.inputTarget?.dataset?.directUploadUrl) {
      console.error("Missing direct upload URL or input target")
      return
    }

    this.handleFiles(e.dataTransfer.files)
  }

  upload(e) {
    this.handleFiles(e.target.files)
  }

  remove(e) {
    e.preventDefault()

    const confirmMessage = e.currentTarget.dataset.confirm
    if (confirmMessage && !window.confirm(confirmMessage)) return

    const row = e.currentTarget.closest("[data-attachment-id], [data-upload-token], [data-signed-id-input-id]")
    if (!row) return

    const signedIdInputId = row.dataset.signedIdInputId

    if (signedIdInputId) {
      document.getElementById(signedIdInputId)?.remove()
      row.remove()
      return
    }

    const destroyUrl = e.currentTarget.dataset.destroyUrl

    if (destroyUrl) {
      fetch(destroyUrl, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        }
      }).then((response) => {
        if (response.ok) row.remove()
      })
    }
  }

  handleFiles(files) {
    Array.from(files).forEach((file) => this.uploadFile(file))
  }

  uploadFile(file) {
    const upload = new DirectUpload(file, this.inputTarget.dataset.directUploadUrl)
    const uploadToken = this.uniqueToken()

    this.showProgress()
    const row = this.appendUploadingFile(file, uploadToken)

    upload.create((error, blob) => {
      this.hideProgress()

      if (error) {
        console.error(error)
        this.showUploadError(row, error)
        return
      }

      if (this.hasUrlValue) {
        this.persistAttachment(blob, row)
      } else {
        const inputId = `attachment_signed_id_${this.uniqueToken()}`
        const hiddenInput = document.createElement("input")
        hiddenInput.type = "hidden"
        hiddenInput.name = this.nameValue
        hiddenInput.value = blob.signed_id
        hiddenInput.id = inputId
        this.hiddenInputsTarget.appendChild(hiddenInput)
        this.replaceUploadingFile(row, blob, inputId)
        this.inputTarget.value = ""
      }
    })
  }

  async persistAttachment(blob, placeholder) {
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          signed_id: blob.signed_id,
          attachable_type: this.attachableTypeValue,
          attachable_id: this.attachableIdValue,
          attribute: this.attributeValue
        })
      })

      if (!response.ok) throw new Error(`Server error: ${response.status}`)

      const attachment = await response.json()
      placeholder.replaceWith(this.buildCard(attachment))
      this.inputTarget.value = ""
    } catch (err) {
      console.error(err)
      this.showUploadError(placeholder, err)
    }
  }

  showUploadError(row, error) {
    if (!row) return

    row.innerHTML = ""
    const message = document.createElement("div")
    message.className = "alert alert-danger py-2 px-3 mb-0 w-100 small"
    message.textContent = this.uploadErrorMessage(error)
    row.appendChild(message)
  }

  uploadErrorMessage(error) {
    const raw = error?.message || String(error)

    try {
      return JSON.parse(raw).error || raw
    } catch (_error) {
      return raw
    }
  }

  buildCard(a) {
    const col = document.createElement("div")
    col.className = "col"
    col.dataset.attachmentId = a.id

    const isImage = a.content_type?.includes("image")

    const preview = a.thumbnail_url
        ? `<img src="${a.thumbnail_url}" class="card-img-top" style="height:80px;object-fit:cover">`
        : `<div class="d-flex align-items-center justify-content-center" style="height:80px;">
           <i class="bi ${this.iconClass(a.content_type)} fs-3"></i>
         </div>`

    const link = isImage
        ? `<a href="${a.url}" class="text-decoration-none"
             data-action="click->attachments#openGallery"
             data-gallery-src="${a.url}"
             data-gallery-filename="${this.escapeHtml(a.filename)}"
             data-gallery-download="${a.download_url}">${preview}</a>`
        : `<a href="${a.url}" target="_blank" class="text-decoration-none">${preview}</a>`

    col.innerHTML = `
      <div class="card position-relative" style="width:120px;overflow:hidden;">
        ${link}
        <div class="position-absolute top-0 end-0 p-1 d-flex gap-1">
          <a href="${a.download_url}" class="btn btn-sm btn-light shadow-sm">
            <i class="bi bi-download"></i>
          </a>
          <a href="#" class="btn btn-sm btn-danger shadow-sm"
             data-action="click->attachments#remove"
             data-destroy-url="${a.destroy_url}">
            <i class="bi bi-trash"></i>
          </a>
        </div>
        <div class="card-footer p-2">
          <div class="small text-truncate" title="${this.escapeHtml(a.filename)}">
            ${this.escapeHtml(a.filename)}
          </div>
        </div>
      </div>
    `
    return col
  }

  appendUploadingFile(file, uploadToken) {
    if (this.hasUrlValue) {
      const col = document.createElement("div")
      col.className = "col"
      col.dataset.uploadToken = uploadToken
      col.innerHTML = `
        <div class="card" style="width:120px;">
          <div class="d-flex align-items-center justify-content-center" style="height:80px;">
            <div class="spinner-border spinner-border-sm text-muted" role="status"></div>
          </div>
          <div class="card-footer p-2">
            <div class="small text-truncate">${this.escapeHtml(file.name)}</div>
          </div>
        </div>
      `
      this.listTarget.appendChild(col)
      return col
    }

    const el = document.createElement("div")
    el.className = "col-12 d-flex align-items-center justify-content-between py-1"
    el.dataset.uploadToken = uploadToken
    el.innerHTML = `
      <span class="text-truncate me-2" style="max-width: 80%">
        <i class="bi bi-arrow-repeat me-1"></i>${this.escapeHtml(file.name)}
      </span>
      <span class="text-muted small">Uploading…</span>
    `
    this.listTarget.appendChild(el)
    return el
  }

  replaceUploadingFile(row, blob, signedIdInputId) {
    const icon = this.iconClass(blob.content_type || "")

    row.dataset.signedIdInputId = signedIdInputId
    row.dataset.persisted = "false"
    row.removeAttribute("data-upload-token")

    row.innerHTML = `
      <span class="text-truncate me-2" style="max-width: 80%">
        <i class="bi ${icon} me-1"></i>${this.escapeHtml(blob.filename)}
      </span>
  
      <div class="d-flex gap-2 flex-shrink-0">
        <a href="#" data-action="click->attachments#remove" class="btn btn-sm btn-danger shadow-sm">
          <i class="bi bi-trash"></i>
        </a>
      </div>
    `
  }

  openGallery(e) {
    e.preventDefault()

    const images = Array.from(this.listTarget.querySelectorAll("[data-gallery-src]"))
    if (images.length === 0) return

    const index = images.indexOf(e.currentTarget)
    this.renderGallery(images, Math.max(index, 0))
  }

  renderGallery(images, index) {
    this.galleryImages = images
    this.galleryIndex = index

    this.galleryCarouselInnerTarget.innerHTML = images.map((link, i) => `
      <div class="carousel-item ${i === index ? "active" : ""}">
        <img src="${link.dataset.gallerySrc}" class="d-block w-100" style="max-height: 80vh; object-fit: contain;" alt="${this.escapeHtml(link.dataset.galleryFilename)}">
      </div>
    `).join("")

    this.galleryPrevTarget.classList.toggle("d-none", images.length < 2)
    this.galleryNextTarget.classList.toggle("d-none", images.length < 2)

    this.updateGalleryMeta()

    window.bootstrap.Modal.getOrCreateInstance(this.galleryModalTarget).show()
  }

  galleryPrev(e) {
    e.preventDefault()
    this.setGalleryIndex(this.galleryIndex - 1)
  }

  galleryNext(e) {
    e.preventDefault()
    this.setGalleryIndex(this.galleryIndex + 1)
  }

  setGalleryIndex(newIndex) {
    if (!this.galleryImages || this.galleryImages.length < 2) return

    const total = this.galleryImages.length
    this.galleryCarouselInnerTarget.children[this.galleryIndex]?.classList.remove("active")
    this.galleryIndex = (newIndex + total) % total
    this.galleryCarouselInnerTarget.children[this.galleryIndex]?.classList.add("active")

    this.updateGalleryMeta()
  }

  updateGalleryMeta() {
    const link = this.galleryImages[this.galleryIndex]

    this.galleryTitleTarget.textContent = link.dataset.galleryFilename
    this.galleryCounterTarget.textContent = this.galleryImages.length > 1 ? `${this.galleryIndex + 1} / ${this.galleryImages.length}` : ""
    this.galleryDownloadTarget.href = link.dataset.galleryDownload
  }

  iconClass(contentType) {
    if (contentType.includes("image")) return "bi-file-image"
    if (contentType.includes("pdf")) return "bi-file-pdf"
    if (contentType.includes("word")) return "bi-file-word"
    if (contentType.includes("excel") || contentType.includes("spreadsheet")) return "bi-file-excel"
    if (contentType.includes("zip")) return "bi-file-zip"
    if (contentType.includes("video")) return "bi-file-play"
    return "bi-file-earmark"
  }

  showProgress() {
    this.progressTarget.classList.remove("d-none")
  }

  hideProgress() {
    this.progressTarget.classList.add("d-none")
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  escapeHtml(value) {
    const div = document.createElement("div")
    div.textContent = value
    return div.innerHTML
  }

  uniqueToken() {
    return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`
  }
}
